import { createSlice, type PayloadAction, createSelector } from "@reduxjs/toolkit";
import type { MenuSorterOptions } from "@/common/AppHeader/mobile/Components/Menu/MenuSorterList";
import type { RootState } from "@/services/store";
import type { FiltersState } from "../types";

const initialState: FiltersState = {
  titleFilter: "",
  noteTitleFilter: "",
  fromDateFilter: "",
  toDateFilter: "",
  personGroupFilters: [],
  noteTypeFilters: [],
  sorter: null,
  stringFilter: "",
};

export const filtersSlice = createSlice({
  name: "contactNotesFilters",
  initialState,
  reducers: {
    setTitleFilter: (state, action: PayloadAction<string>) => {
      state.titleFilter = action.payload;
    },
    setNoteTitleFilter: (state, action: PayloadAction<string>) => {
      state.noteTitleFilter = action.payload;
    },
    setFromDateFilter: (state, action: PayloadAction<string>) => {
      state.fromDateFilter = action.payload;
    },
    setToDateFilter: (state, action: PayloadAction<string>) => {
      state.toDateFilter = action.payload;
    },
    setPersonGroupFilters: (state, action: PayloadAction<string[]>) => {
      state.personGroupFilters = action.payload;
    },
    setNoteTypeFilters: (state, action: PayloadAction<string[]>) => {
      state.noteTypeFilters = action.payload;
    },
    setSorter: (state, action: PayloadAction<MenuSorterOptions>) => {
      state.sorter = action.payload;
    },
    setStringFilter: (state, action: PayloadAction<string>) => {
      state.stringFilter = action.payload;
    },
    clearFilters: (state) => {
      Object.assign(state, initialState);
    },
  },
});

const selectFiltersState = (state: RootState) => state.contactNotes.filters;

export const selectTitleFilter = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.titleFilter,
);

export const selectNoteTitleFilter = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.noteTitleFilter,
);

export const selectFromDateFilter = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.fromDateFilter,
);

export const selectToDateFilter = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.toDateFilter,
);

export const selectPersonGroupFilters = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.personGroupFilters,
);

export const selectNoteTypeFilters = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.noteTypeFilters,
);

export const selectSorter = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.sorter,
);

export const selectStringFilter = createSelector(
  [selectFiltersState],
  (filtersState) => filtersState.stringFilter,
);

export const hasFilters = createSelector(
  [selectFiltersState],
  (filtersState) =>
    filtersState.titleFilter.length > 0 ||
    filtersState.noteTitleFilter.length > 0 ||
    (filtersState.fromDateFilter?.length ?? 0) > 0 ||
    (filtersState.toDateFilter?.length ?? 0) > 0 ||
    filtersState.personGroupFilters.length > 0 ||
    filtersState.stringFilter.length > 0,
);

export const filtersSliceSelectors = {
  selectTitleFilter,
  selectNoteTitleFilter,
  selectFromDateFilter,
  selectToDateFilter,
  selectPersonGroupFilters,
  selectNoteTypeFilters,
  selectSorter,
  selectStringFilter,
  hasFilters,
};
